import { useEffect, useState } from 'react';
import { ITask } from "../data_models/task";
import useCurrentUser from "../hooks/useCurrentUser";
import { FreelancerExperienceLevel, FreelancerRole } from "../data_models/freelancer";

import { OutlinedDropdownMenu } from "../components/common/DropdownMenu";
import { getNumericEnumValues } from "../utils/enumUtils";
import { UserEnumTextMapper } from "../data_models/enumToTextMapper";
import MultiTextSelect from "../components/common/MultiTextSelect";
import BriefTaskComponent from '../components/common/tasks/BriefTaskComponent';
import useTasks from '../hooks/useTasks';
import useGlobalLoading from '../hooks/useGlobalLoading';


const JobsPage: React.FC = () => {

    const { fetchAvailableTasksAsFreelancerByFilter } = useTasks();
    const { currentUser, updateCurrentUser } = useCurrentUser();
    const { setLoading } = useGlobalLoading();

    const [experienceFilter, setExperienceFilter] = useState(FreelancerExperienceLevel.T0);
    const [skillsFilter, setSkillsFilter] = useState<string[]>([]);
    const [toolsFilter, setToolsFilter] = useState<string[]>([]);
    const [roleFilter, setRoleFilter] = useState<FreelancerRole>();

    const [filter, setFilter] = useState<any>({});

    const [tasks, setTasks] = useState<ITask[]>([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);

    const fetchTasks = async () => {
        const tasks = await fetchAvailableTasksAsFreelancerByFilter(page, limit, filter);
        setTasks(tasks);
    }

    useEffect(() => {
        const filter = {
            ...(experienceFilter !== undefined && {
                requiredExperience: { $gte: experienceFilter }, // At least the given experience
            }),
            ...(skillsFilter.length > 0 && {
                requiredSkills: {
                    $elemMatch: {
                        $regex: skillsFilter.map(skill => `.*${skill}.*`).join('|'),
                        $options: 'i'
                    }
                }
            }),
            ...(toolsFilter.length > 0 && {
                requiredTools: {
                    $elemMatch: {
                        $regex: toolsFilter.map(tool => `.*${tool}.*`).join('|'),
                        $options: 'i'
                    }
                }
            }),
            ...(roleFilter !== undefined && roleFilter !== FreelancerRole.OTHER && { requiredRole: roleFilter }) // Only include if roleFilter is set and not "other"
        };
        setFilter(filter);
    }, [experienceFilter,
        skillsFilter,
        toolsFilter,
        roleFilter])


    useEffect(() => {
        const fetch = async () => {
            setLoading(true);
            await fetchTasks();
            setLoading(false);
        }
        fetch();
    }, [filter]);

    return (
        <div className="p-4 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10">
                {/* Filter column */}
                <div className="col-span-1">
                    <p className="body2 font-bold">Filter</p>

                    <div>
                        {/* Freelancer Role */}
                        <OutlinedDropdownMenu
                            options={getNumericEnumValues(FreelancerRole)}
                            optionsToTextMapper={UserEnumTextMapper.freelancerRole}
                            defaultValue={roleFilter}
                            onSelect={(value) => {
                                const val = value as unknown as FreelancerRole;
                                setRoleFilter(val);
                            }}
                            label="Role"
                        />


                        {/* Experience */}
                        <OutlinedDropdownMenu
                            options={getNumericEnumValues(FreelancerExperienceLevel)}
                            optionsToTextMapper={UserEnumTextMapper.freelancerExperienceLevel}
                            defaultValue={experienceFilter}
                            onSelect={(value) => {
                                const val = value as unknown as FreelancerExperienceLevel;
                                setExperienceFilter(val);
                            }}
                            label="Experience"
                        />


                        {/* Skills */}
                        <MultiTextSelect
                            elements={skillsFilter}
                            setElements={setSkillsFilter}
                            maxNumSelections={3}
                            label="Skills"
                            placeholder="Select Skills"
                        />

                        {/* Tools */}
                        <MultiTextSelect
                            elements={toolsFilter}
                            setElements={setToolsFilter}
                            maxNumSelections={3}
                            label="Tools"
                            placeholder="Select Tool"
                        />


                    </div>
                </div>

                {/* Tasks column */}
                <div className="col-span-full sm:col-span-1 md:col-span-3">
                    {/* List of tasks */}

                    {page === 1 && tasks.length === 0
                        ? <div className="mt-4 flex flex-col items-center space-y-4">
                            <img
                                src="https://storage.googleapis.com/public-platform-assets/images/no_data.svg"
                                alt="No Data"
                                className="w-2/3"
                            />
                            <p className="body1 max-w-lg text-center">We are sorry, but at this moment we couldn't find any tasks that match your filter</p>
                        </div>
                        : <div className="">
                            {tasks.map((task) => (
                                <div key={task._id}>
                                    <BriefTaskComponent task={task} />
                                </div>
                            ))}

                        </div >}

                </div>
            </div>
        </div >
    );
}

export default JobsPage;
